<template>
<b-row class="ot-main-panel no-gutters">
    <b-col>
        <template v-if="loaded === false">
            <div class="OTloading">
                <div class="OTloader"></div>
            </div>
        </template>
        <CollectionTable v-else
            :collection="collection"
            :actions="actions"
            :filters="filters"
            :globalActions="globalActions"
            :columns="columns">
            <template slot="table-header">
                <h1 class="text-center OT-table-title">
                    <span class="OTtableTitleBreadCrumb" v-if="project_id">
                        <otif-icon icon="project" title="Project"/>
                        <span class="OTtableTitleBreadCrumbText"> {{ project.get('project_name') }} </span>
                        <otif-icon icon="job" title="Job"/>
                        <otif-icon icon="project-separator"/>
                        <span class="OTtableTitleBreadCrumbTextJob">Jobs List</span>
                    </span>
                    <span v-else>
                        Jobs List
                    </span>
                </h1>
            </template>
            <template slot="empty">
                No jobs available. Click <a @click="createJob">here</a> to create a new job.
            </template>
        </CollectionTable>
    </b-col>
</b-row>
</template>

<script>

import axios from 'axios';
import moment from 'moment';

import FacetContainer from '@/components/widgets/FacetContainer.vue';
import ProjectFacets from '@/components/jobsList/ProjectFacets.vue';
import JobActions from '@/components/JobActions.vue';
import DeleteJobModal from '@/components/modals/ConfirmDeleteJob.vue';
import CreateJobModal from '@/components/modals/CreateJob.vue';
import JobsCollection from '@/collections/jobs.js';
import CollectionTable from '@/components/CollectionTable.vue';
import JobNameColumn from '@/components/jobsList/JobNameColumn.vue';
import JobTypeColumn from '@/components/jobsList/JobTypeColumn.vue';
import Project from '@/models/project.js';
import Projects from '@/collections/projects.js';

export default {
    mixins: [JobActions],
    props: ['project_id'],
    data() {
        return {
            loaded: false,
            project: null,
            projects: new Projects(),
            showCreateJobModal: false,
            confirmDelete: false,
            confirmDeleteJobName: '',
            confirmDeleteJobId: -1,
            filters: [{
                name: 'project_id',
                label: 'Projects',
                values: []
            },{
                name: 'status',
                label: 'Status',
                values: [
                    'Running',
                    'Completed',
                    'Failed',
                    'Aborted',
                    'Scheduled',
                    'Unscheduled',
                    'Waiting'
                ]
            },{
                name: 'job_type',
                label: 'Job Type',
                values: [
                    'Twitter Search',
                    'Twitter Stream',
                    'Web',
                    'ECM',
                    'RDBMS'
                ]
            }],
            globalActions: [{
                name: 'add',
                label: 'Create New Job',
                callback: () => {
                    this.createJob();
                }
            }],
            columns: [{
                name: 'job_id',
                label: 'ID',
                sortable: true,
                render(row) {
                    return row.attributes.job_id;
                }
            },{
                name: 'job_name',
                label: 'Name',
                sortable: true,
                displayComponent: JobNameColumn
            },{
                name: 'project_name',
                label: 'Project',
                sortable: true,
                render(row) {
                    return row.attributes.project_name;
                }
            },{
                name: 'job_type',
                label: 'Type',
                sortable: true,
                displayComponent: JobTypeColumn
            },{
                name: 'status',
                label: 'Status',
                sortable: true,
                render(row) {
                    return row.attributes.status;
                }
            },{
                name: 'modified_date',
                label: 'Last Modified',
                sortable: true,
                render(row) {
                    return moment(row.get('modified_date', moment.ISO_8601))
                    .format('MMMM Do YYYY @ h:ma');
                }
            }],
            statusFacets: [{
                name: 'status',
                value: 'Running'
            },{
                name: 'status',
                value: 'Completed'
            },{
                name: 'status',
                value: 'Waiting'
            },{
                name: 'status',
                value: 'Failed'
            },{
                name: 'status',
                value: 'Aborted'
            },{
                name: 'status',
                value: 'Scheduled'
            },{
                name: 'status',
                value: 'Unscheduled'
            }],
            jobTypeFacets: [{
                name: 'job_type',
                value: 'Twitter Search'
            },{
                name: 'job_type',
                value: 'Twitter Stream'
            },{
                name: 'job_type',
                value: 'Web'
            },{
                name: 'job_type',
                value: 'ECM'
            }],
            actions: [{
                name: 'start',
                label: 'Start',
                callback: this.onStart
            },{
                name: 'stop',
                label: 'Stop',
                callback: this.onStop
            },{
                name: 'edit',
                label: 'Edit',
                callback: this.onEdit
            },{
                name: 'delete',
                label: 'Delete',
                callback: this.onDelete
            },{
                name: 'clone',
                label: 'Clone',
                callback: this.onClone
            }],
            collection: new JobsCollection({
                paginated: true
            })
        }
    },

    computed: {
        jobs() {
            return this.collection.resources;
        }
    },

    watch: {
        project_id() {
            if (!this.project_id) {
                this.project = null;
            }
            this.refresh();
        }
    },

    methods: {
        onClone(rowNumber, rows) {
            const job = rows[rowNumber];
            this.$eventBus.$emit('clone-job', job);
        },

        onStart(rowNumber, rows) {
            const job = rows[rowNumber];
            this.startJob(job);
        },

        createJob() {
            this.$eventBus.$emit('create-job');
        },

        onStop(rowNumber, rows) {
            const job = rows[rowNumber];
            this.stopJob(job);
        },

        onEdit(rowNumber, rows) {
            const job = rows[rowNumber];
            const job_id = rows[rowNumber].attributes.job_id;

            this.$router.push('/jobs/' + encodeURIComponent(job_id) + '/general-settings');
        },

        onDelete(rowNumber, rows) {
            const job = rows[rowNumber];
            this.$eventBus.$emit('delete-job', job);
        },

        addFilter(facet) {
            this.collection.query(facet.name, facet.value);
        },

        removeFilter(facet) {
            this.collection.removeQuery(facet.name, facet.value);
        },

        refreshBreadcrumbs() {
            var breadcrumbs = [];

            if (this.project_id) {
                breadcrumbs = [{
                    name: 'projects',
                    meta: {
                        label: 'Projects'
                    }
                },{
                    name: 'projectJobs',
                    params: {
                        project_id: this.project_id
                    },
                    meta: {
                        label: this.project.get('project_name')
                    }
                }];
            } else {
                breadcrumbs = [{
                    name: 'jobs',
                    meta: {
                        label: 'Jobs'
                    }
                }];
            }
            this.$store.commit('breadcrumbs', breadcrumbs);

        },

        refresh() {
            if (this.project_id) {
                this.project = new Project();
                return this.project.fetch(this.project_id)
                .then(() => {
                    this.refreshBreadcrumbs();
                    this.collection.setFilter('project_id', this.project_id);
                    return this.collection.fetch({
                        params: {
                            sort: "modified_date:desc"
                        }
                    });
                })
                .then(() => {
                    this.loaded = true;
                })

            } else {
                this.collection.setFilters({});
                return this.collection.fetch({
                    params: {
                        sort: "modified_date:desc"
                    }
                })
                .then(() => {
                    this.refreshBreadcrumbs();
                    this.loaded = true;
                });
            }
        }
    },

    components: {
        FacetContainer,
        ProjectFacets,
        DeleteJobModal,
        CreateJobModal,
        CollectionTable
    },

    mounted() {
        this.$eventBus.$on('job-deleted', () => {
            this.refresh();
        });

        this.refresh();
    },

    created() {
        this.projects.fetch()
        .then(() => {
            this.filters[0]['values'] = this.projects.resources.map((project) => {
                return {
                    value: project.id,
                    label: project.get('project_name')
                };
            });
        });
    }
}

</script>
